export function properNameCase(name: string): string {
  const names = name.split(" ");
  let properNames: string[] = [];
  names.forEach((currentName) => {
    currentName = currentName.toLowerCase();
    switch (currentName) {
      case "de":
      case "van":
      case "von":
        properNames.push(currentName);
        break;

      default:
        properNames.push(
          currentName.charAt(0).toUpperCase() +
            currentName.substr(1).toLowerCase()
        );
        break;
    }
  });
  return properNames.join(" ");
}

export function snakeCaseToPascalCase(text: string) {
  return text
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
}

// --- Pluralization logic (adapted from the pluralize npm package) ---

type _PluralizationRule = [RegExp, string];

const _pluralRules: _PluralizationRule[] = [];
const _singularRules: _PluralizationRule[] = [];
const _uncountables: Record<string, boolean> = {};
const _irregularPlurals: Record<string, string> = {};
const _irregularSingles: Record<string, string> = {};

function _sanitizeRule(rule: RegExp | string): RegExp {
  return typeof rule === 'string' ? new RegExp('^' + rule + '$', 'i') : rule;
}

function _restoreCase(word: string, token: string): string {
  if (word === token) return token;
  if (word === word.toLowerCase()) return token.toLowerCase();
  if (word === word.toUpperCase()) return token.toUpperCase();
  if (word[0] === word[0].toUpperCase()) {
    return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
  }
  return token.toLowerCase();
}

function _interpolate(str: string, args: string[]): string {
  return str.replace(/\$(\d{1,2})/g, (_m, i: string) => args[Number(i)] ?? '');
}

function _applyRule(word: string, rule: _PluralizationRule): string {
  return word.replace(rule[0], (...args: any[]): string => {
    const match = args[0] as string;
    const strArgs: string[] = args.map((a) => (a == null ? '' : String(a)));
    const result = _interpolate(rule[1], strArgs);
    if (match === '') {
      const offset = args[args.length - 2] as number;
      return _restoreCase(word[offset - 1], result);
    }
    return _restoreCase(match, result);
  });
}

function _sanitizeWord(token: string, word: string, rules: _PluralizationRule[]): string {
  if (!token.length || Object.prototype.hasOwnProperty.call(_uncountables, token)) {
    return word;
  }
  let len = rules.length;
  while (len--) {
    const rule = rules[len];
    if (rule[0].test(word)) return _applyRule(word, rule);
  }
  return word;
}

function _replaceWord(
  replaceMap: Record<string, string>,
  keepMap: Record<string, string>,
  rules: _PluralizationRule[]
): (word: string) => string {
  return (word: string): string => {
    const token = word.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(keepMap, token)) return _restoreCase(word, token);
    if (Object.prototype.hasOwnProperty.call(replaceMap, token)) return _restoreCase(word, replaceMap[token]);
    return _sanitizeWord(token, word, rules);
  };
}

(([
  ['I', 'we'], ['me', 'us'], ['he', 'they'], ['she', 'they'], ['them', 'them'],
  ['myself', 'ourselves'], ['yourself', 'yourselves'], ['itself', 'themselves'],
  ['herself', 'themselves'], ['himself', 'themselves'], ['themself', 'themselves'],
  ['is', 'are'], ['was', 'were'], ['has', 'have'], ['this', 'these'], ['that', 'those'],
  ['my', 'our'], ['its', 'their'], ['his', 'their'], ['her', 'their'],
  ['echo', 'echoes'], ['dingo', 'dingoes'], ['volcano', 'volcanoes'],
  ['tornado', 'tornadoes'], ['torpedo', 'torpedoes'],
  ['genus', 'genera'], ['viscus', 'viscera'],
  ['stigma', 'stigmata'], ['stoma', 'stomata'], ['dogma', 'dogmata'],
  ['lemma', 'lemmata'], ['schema', 'schemata'], ['anathema', 'anathemata'],
  ['ox', 'oxen'], ['axe', 'axes'], ['die', 'dice'], ['yes', 'yeses'],
  ['foot', 'feet'], ['eave', 'eaves'], ['goose', 'geese'], ['tooth', 'teeth'],
  ['quiz', 'quizzes'], ['human', 'humans'], ['proof', 'proofs'], ['carve', 'carves'],
  ['valve', 'valves'], ['looey', 'looies'], ['thief', 'thieves'], ['groove', 'grooves'],
  ['pickaxe', 'pickaxes'], ['passerby', 'passersby'], ['canvas', 'canvases'],
] as [string, string][]).forEach(([single, plur]) => {
  const p = plur.toLowerCase();
  const s = single.toLowerCase();
  _irregularSingles[s] = p;
  _irregularPlurals[p] = s;
}));

(([
  [/s?$/i, 's'],
  [/[^\u0000-\u007F]$/i, '$0'],
  [/([^aeiou]ese)$/i, '$1'],
  [/(ax|test)is$/i, '$1es'],
  [/(alias|[^aou]us|t[lm]as|gas|ris)$/i, '$1es'],
  [/(e[mn]u)s?$/i, '$1s'],
  [/([^l]ias|[aeiou]las|[ejzr]as|[iu]am)$/i, '$1'],
  [/(alumn|syllab|vir|radi|nucle|fung|cact|stimul|termin|bacill|foc|uter|loc|strat)(?:us|i)$/i, '$1i'],
  [/(alumn|alg|vertebr)(?:a|ae)$/i, '$1ae'],
  [/(seraph|cherub)(?:im)?$/i, '$1im'],
  [/(her|at|gr)o$/i, '$1oes'],
  [/(agend|addend|millenni|dat|extrem|bacteri|desiderat|strat|candelabr|errat|ov|symposi|curricul|automat|quor)(?:a|um)$/i, '$1a'],
  [/(apheli|hyperbat|periheli|asyndet|noumen|phenomen|criteri|organ|prolegomen|hedr|automat)(?:a|on)$/i, '$1a'],
  [/sis$/i, 'ses'],
  [/(?:(kni|wi|li)fe|(ar|l|ea|eo|oa|hoo)f)$/i, '$1$2ves'],
  [/([^aeiouy]|qu)y$/i, '$1ies'],
  [/([^ch][ieo][ln])ey$/i, '$1ies'],
  [/(x|ch|ss|sh|zz)$/i, '$1es'],
  [/(matr|cod|mur|sil|vert|ind|append)(?:ix|ex)$/i, '$1ices'],
  [/\b((?:tit)?m|l)(?:ice|ouse)$/i, '$1ice'],
  [/(pe)(?:rson|ople)$/i, '$1ople'],
  [/(child)(?:ren)?$/i, '$1ren'],
  [/eaux$/i, '$0'],
  [/m[ae]n$/i, 'men'],
  ['thou', 'you'],
] as [RegExp | string, string][]).forEach(([rule, replacement]) => {
  _pluralRules.push([_sanitizeRule(rule), replacement]);
}));

(([
  [/s$/i, ''],
  [/(ss)$/i, '$1'],
  [/(wi|kni|(?:after|half|high|low|mid|non|night|[^\w]|^)li)ves$/i, '$1fe'],
  [/(ar|(?:wo|[ae])l|[eo][ao])ves$/i, '$1f'],
  [/ies$/i, 'y'],
  [/(dg|ss|ois|lk|ok|wn|mb|th|ch|ec|oal|is|ck|ix|sser|ts|wb)ies$/i, '$1ie'],
  [/\b(l|(?:neck|cross|hog|aun)?t|coll|faer|food|gen|goon|group|hipp|junk|vegg|(?:pork)?p|charl|calor|cut)ies$/i, '$1ie'],
  [/\b(mon|smil)ies$/i, '$1ey'],
  [/\b((?:tit)?m|l)ice$/i, '$1ouse'],
  [/(seraph|cherub)im$/i, '$1'],
  [/(x|ch|ss|sh|zz|tto|go|cho|alias|[^aou]us|t[lm]as|gas|(?:her|at|gr)o|[aeiou]ris)(?:es)?$/i, '$1'],
  [/(analy|diagno|parenthe|progno|synop|the|empha|cri|ne)(?:sis|ses)$/i, '$1sis'],
  [/(movie|twelve|abuse|e[mn]u)s$/i, '$1'],
  [/(test)(?:is|es)$/i, '$1is'],
  [/(alumn|syllab|vir|radi|nucle|fung|cact|stimul|termin|bacill|foc|uter|loc|strat)(?:us|i)$/i, '$1us'],
  [/(agend|addend|millenni|dat|extrem|bacteri|desiderat|strat|candelabr|errat|ov|symposi|curricul|quor)a$/i, '$1um'],
  [/(apheli|hyperbat|periheli|asyndet|noumen|phenomen|criteri|organ|prolegomen|hedr|automat)a$/i, '$1on'],
  [/(alumn|alg|vertebr)ae$/i, '$1a'],
  [/(cod|mur|sil|vert|ind)ices$/i, '$1ex'],
  [/(matr|append)ices$/i, '$1ix'],
  [/(pe)(rson|ople)$/i, '$1rson'],
  [/(child)ren$/i, '$1'],
  [/(eau)x?$/i, '$1'],
  [/men$/i, 'man'],
] as [RegExp | string, string][]).forEach(([rule, replacement]) => {
  _singularRules.push([_sanitizeRule(rule), replacement]);
}));

(([
  'adulthood', 'advice', 'agenda', 'aid', 'aircraft', 'alcohol', 'ammo', 'analytics',
  'anime', 'athletics', 'audio', 'bison', 'blood', 'bream', 'buffalo', 'butter',
  'carp', 'cash', 'chassis', 'chess', 'clothing', 'cod', 'commerce', 'cooperation',
  'corps', 'debris', 'diabetes', 'digestion', 'elk', 'energy', 'equipment',
  'excretion', 'expertise', 'firmware', 'flounder', 'fun', 'gallows', 'garbage',
  'graffiti', 'hardware', 'headquarters', 'health', 'herpes', 'highjinks', 'homework',
  'housework', 'information', 'jeans', 'justice', 'kudos', 'labour', 'literature',
  'machinery', 'mackerel', 'mail', 'media', 'mews', 'moose', 'music', 'mud', 'manga',
  'news', 'only', 'personnel', 'pike', 'plankton', 'pliers', 'police', 'pollution',
  'premises', 'rain', 'research', 'rice', 'salmon', 'scissors', 'series', 'sewage',
  'shambles', 'shrimp', 'software', 'staff', 'swine', 'tennis', 'traffic',
  'transportation', 'trout', 'tuna', 'wealth', 'welfare', 'whiting', 'wildebeest',
  'wildlife', 'you',
  /pok[eé]mon$/i,
  /[^aeiou]ese$/i,
  /deer$/i,
  /fish$/i,
  /measles$/i,
  /o[iu]s$/i,
  /pox$/i,
  /sheep$/i,
] as (string | RegExp)[]).forEach((word) => {
  if (typeof word === 'string') {
    _uncountables[word.toLowerCase()] = true;
  } else {
    _pluralRules.push([_sanitizeRule(word), '$0']);
    _singularRules.push([_sanitizeRule(word), '$0']);
  }
}));

const _plural = _replaceWord(_irregularSingles, _irregularPlurals, _pluralRules);
const _singular = _replaceWord(_irregularPlurals, _irregularSingles, _singularRules);

export function pluralize(word: string, count?: number, inclusive?: boolean): string {
  const pluralized = count === 1 ? _singular(word) : _plural(word);
  return (inclusive ? count + ' ' : '') + pluralized;
}

export function Pluralize(word: string): string {
  return _plural(word);
}

export function singular(word: string): string {
  return _singular(word);
}
